import axios from "axios";
import configs from "../../../configs/config";
import LanguageData from "../../../configs/LanguageData.json";
import { ActionType } from "../Snackbars";
import { triggerAppAlert } from "../SelfServicePortal/ContactAid/ContactAid";

/**
 * IMPORTANT:
 * In your project, configs.BASE_URL usually points to the API Gateway base.
 * Example:
 *   configs.BASE_URL = http://localhost:3000/gateway
 * or sometimes:
 *   configs.BASE_URL = http://localhost:3000
 *
 * This helper builds a correct AzureUser gateway base:
 * - if BASE_URL already contains "/gateway" => append "/azureuser"
 * - else append "/gateway/azureuser"
 */
const normalizeBase = (baseUrl: string) => (baseUrl || "").replace(/\/+$/, "");
const base = normalizeBase(configs.BASE_URL);
const hasGateway = base.toLowerCase().includes("/gateway");
export const azureBaseUrl = hasGateway ? `${base}/azureuser` : `${base}/gateway/azureuser`;

// ---- endpoints ----
const usersUrl = `${azureBaseUrl}/users`;
const groupsUrl = `${azureBaseUrl}/groups`;
const subscribedSkusUrl = `${azureBaseUrl}/licenses/subscribedSkus`;
const deletedUsersUrl = `${azureBaseUrl}/deletedUsers`;

// ---- action types (use in reducer) ----
export const AZURE_USERS_LOADING = "AZURE_USERS_LOADING";
export const AZURE_USERS_ERROR = "AZURE_USERS_ERROR";
export const GET_AZURE_USERS = "GET_AZURE_USERS";
export const APPEND_AZURE_USERS = "APPEND_AZURE_USERS";

export const AZURE_USER_DETAILS_LOADING = "AZURE_USER_DETAILS_LOADING";
export const AZURE_USER_DETAILS_ERROR = "AZURE_USER_DETAILS_ERROR";
export const GET_AZURE_USER_DETAILS = "GET_AZURE_USER_DETAILS";

export const AZURE_GROUPS_LOADING = "AZURE_GROUPS_LOADING";
export const AZURE_GROUPS_ERROR = "AZURE_GROUPS_ERROR";
export const GET_AZURE_GROUPS = "GET_AZURE_GROUPS";

export const AZURE_SKUS_LOADING = "AZURE_SKUS_LOADING";
export const AZURE_SKUS_ERROR = "AZURE_SKUS_ERROR";
export const GET_AZURE_SKUS = "GET_AZURE_SKUS";

// ✅ add these aliases so reducer imports work
export const AZURE_USERS_RESET = "AZURE_USERS_RESET";
export const AZURE_USERS_SUCCESS = "AZURE_USERS_SUCCESS";
export const AZURE_USERS_APPEND = "AZURE_USERS_APPEND";

export const AZURE_USER_DETAILS_SUCCESS = "AZURE_USER_DETAILS_SUCCESS";
export const AZURE_GROUPS_SUCCESS = "AZURE_GROUPS_SUCCESS";
export const AZURE_SKUS_SUCCESS = "AZURE_SKUS_SUCCESS";

export const AZURE_USER_DETAILS_RESET = "AZURE_USER_DETAILS_RESET";

export const AZURE_DOMAINS_LOADING = "AZURE_DOMAINS_LOADING";
export const AZURE_DOMAINS_SUCCESS = "AZURE_DOMAINS_SUCCESS";
export const AZURE_DOMAINS_ERROR = "AZURE_DOMAINS_ERROR";

// ✅ small helper to reset list in reducer
export const resetAzureUsers = () => ({
  type: AZURE_USERS_RESET,
});
// ---- basic helpers ----
const headerBool = (v: any) => String(v || "").toLowerCase() === "true";
const getHdr = (headers: any, key: string) => headers?.[key.toLowerCase()] ?? headers?.[key] ?? null;

export const azureUsersLoading = (status: boolean) => ({
  type: AZURE_USERS_LOADING,
  payload: status,
});

export const azureUserDetailsLoading = (status: boolean) => ({
  type: AZURE_USER_DETAILS_LOADING,
  payload: status,
});

export const azureGroupsLoading = (status: boolean) => ({
  type: AZURE_GROUPS_LOADING,
  payload: status,
});

export const azureSkusLoading = (status: boolean) => ({
  type: AZURE_SKUS_LOADING,
  payload: status,
});

// =====================================================================================
// ====================================== USERS =======================================
// =====================================================================================

/**
 * GET Azure Users
 * Backend: GET /api/AzureUser/users?status=all|active|inactive|deleted&pageSize=50&continuationToken=...&search=...&includeGuests=false
 * Gateway: /gateway/azureuser/users ...
 */
export const getAzureUsers = (
  params?: {
    status?: "all" | "active" | "inactive" | "deleted";
    pageSize?: number;
    continuationToken?: string | null;
    search?: string | null;
    includeGuests?: boolean;
    append?: boolean;
  },
  callback?: (status: "success" | "failure", data?: any) => void
) => {
  return async (dispatch: any) => {
    try {
      dispatch(azureUsersLoading(true));

      const query = {
        status: params?.status ?? "all",
        pageSize: params?.pageSize ?? 50,
        includeGuests: params?.includeGuests ?? false,
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.continuationToken ? { continuationToken: params.continuationToken } : {}),
      };

      const response = await axios.get(usersUrl, { params: query });

      // pagination headers from backend
      const continuationToken = getHdr(response.headers, "x-continuation-token");
      const hasMore = headerBool(getHdr(response.headers, "x-has-more"));
      const pageSize = Number(getHdr(response.headers, "x-page-size") || query.pageSize);
      const resultCount = Number(getHdr(response.headers, "x-result-count") || (response.data?.length || 0));

      dispatch(azureUsersLoading(false));

      dispatch({
        // type: params?.append ? APPEND_AZURE_USERS : GET_AZURE_USERS,
        type: params?.append ? AZURE_USERS_APPEND : AZURE_USERS_SUCCESS,
        payload: {
          data: response.data,
          meta: { continuationToken, hasMore, pageSize, resultCount, status: query.status, search: query.search || "" },
        },
      });

      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch(azureUsersLoading(false));
      dispatch({
        type: AZURE_USERS_ERROR,
        payload: error?.response?.data || error?.message || "Something went wrong!",
      });

      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Unable To Fetch Azure Users.",
          severity: "error",
          open: true,
        },
      });

      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const getAzureUserDetails = (userId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({ type: AZURE_USER_DETAILS_RESET });
      dispatch(azureUserDetailsLoading(true));

      const response = await axios.get(`${usersUrl}/${userId}/details`);

      dispatch(azureUserDetailsLoading(false));
      dispatch({ type: AZURE_USER_DETAILS_SUCCESS, payload: response.data });

      if (callback) callback("success", response.data);
      return response;
    } catch (error: any) {
      dispatch(azureUserDetailsLoading(false));
      dispatch({
        type: AZURE_USER_DETAILS_ERROR,
        payload: error?.response?.data || error?.message,
      });

      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Unable To Fetch Azure User Details.",
          severity: "error",
          open: true,
        },
      });

      if (callback) callback("failure", error?.response?.data);
      return { error };
    }
  };
};

export const createAzureUser = (data: any, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.post(usersUrl, data, {
        headers: { "Content-Type": "application/json" },
      });

      dispatch(triggerAppAlert("Azure User Created Successfully", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Create Azure User.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const updateAzureUser = (userId: string, data: any, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.patch(`${usersUrl}/${userId}`, data, {
        headers: { "Content-Type": "application/json" },
      });

      dispatch(triggerAppAlert(`${LanguageData.TICKET_UPDATED || "Updated"} successfully`, "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Update Azure User.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const disableAzureUser = (userId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.patch(`${usersUrl}/${userId}/disable`);
      dispatch(triggerAppAlert("User Disabled Successfully", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Disable User.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const enableAzureUser = (userId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.patch(`${usersUrl}/${userId}/enable`);
      dispatch(triggerAppAlert("User Enabled Successfully", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Enable User.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const softDeleteAzureUser = (userId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.delete(`${usersUrl}/${userId}`);
      dispatch(triggerAppAlert("User Deleted Successfully", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Delete User.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const restoreAzureUser = (deletedObjectId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.post(`${deletedUsersUrl}/${deletedObjectId}/restore`);
      dispatch(triggerAppAlert("User Restored Successfully", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Restore User.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const hardDeleteAzureUser = (deletedObjectId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.delete(`${deletedUsersUrl}/${deletedObjectId}`);
      dispatch(triggerAppAlert("User Permanently Deleted", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Hard Delete User.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

// =====================================================================================
// ====================================== GROUPS ======================================
// =====================================================================================

export const getAzureGroups = (
  params?: { pageSize?: number; continuationToken?: string | null; search?: string | null },
  callback?: any
) => {
  return async (dispatch: any) => {
    try {
      dispatch(azureGroupsLoading(true));

      const query: any = {
        pageSize: params?.pageSize ?? 50,
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.continuationToken ? { continuationToken: params.continuationToken } : {}),
      };

      const response = await axios.get(groupsUrl, { params: query });

      dispatch(azureGroupsLoading(false));
      dispatch({ type: AZURE_GROUPS_SUCCESS, payload: response.data });

      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch(azureGroupsLoading(false));
      dispatch({ type: AZURE_GROUPS_ERROR, payload: error?.response?.data || error?.message });

      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Unable To Fetch Groups.",
          severity: "error",
          open: true,
        },
      });

      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const createAzureGroup = (data: any, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.post(groupsUrl, data, {
        headers: { "Content-Type": "application/json" },
      });

      dispatch(triggerAppAlert("Group Created Successfully", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Create Group.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const updateAzureGroup = (groupId: string, data: any, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.patch(`${groupsUrl}/${groupId}`, data, {
        headers: { "Content-Type": "application/json" },
      });

      dispatch(triggerAppAlert("Group Updated Successfully", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Update Group.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const deleteAzureGroup = (groupId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.delete(`${groupsUrl}/${groupId}`);
      dispatch(triggerAppAlert("Group Deleted Successfully", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Delete Group.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const addUserToGroup = (groupId: string, userId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.post(`${groupsUrl}/${groupId}/members/${userId}`);
      dispatch(triggerAppAlert("User Added To Group", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Add User To Group.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const removeUserFromGroup = (groupId: string, userId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.delete(`${groupsUrl}/${groupId}/members/${userId}`);
      dispatch(triggerAppAlert("User Removed From Group", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Remove User From Group.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

// =====================================================================================
// ===================================== LICENSES =====================================
// =====================================================================================

export const getSubscribedSkus = (callback?: any) => {
  return async (dispatch: any) => {
    try {
      dispatch(azureSkusLoading(true));
      const response = await axios.get(subscribedSkusUrl);
      dispatch(azureSkusLoading(false));
      dispatch({ type: AZURE_SKUS_SUCCESS, payload: response.data });
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch(azureSkusLoading(false));
      dispatch({ type: AZURE_SKUS_ERROR, payload: error?.response?.data || error?.message });

      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Unable To Fetch Licenses.",
          severity: "error",
          open: true,
        },
      });

      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const getUserLicenseDetails = (userId: string, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.get(`${usersUrl}/${userId}/licenseDetails`);
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Unable To Fetch User License Details.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

export const assignLicense = (userId: string, data: any, callback?: any) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.post(`${usersUrl}/${userId}/assignLicense`, data, {
        headers: { "Content-Type": "application/json" },
      });

      dispatch(triggerAppAlert("License Changes Applied Successfully", "success"));
      if (callback) callback("success", response.data);
    } catch (error: any) {
      dispatch({
        type: ActionType.SNACKBAR,
        payload: {
          message:
            error?.response?.data && typeof error.response.data === "string"
              ? error.response.data
              : "Failed To Assign/Remove License.",
          severity: "error",
          open: true,
        },
      });
      if (callback) callback("failure", error?.response?.data);
    }
  };
};

// =====================================================================================
// ===================================== DOMAIN ========================================
// =====================================================================================

export const getAzureDomains = (callback?: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({ type: AZURE_DOMAINS_LOADING, payload: true });

      const response = await axios.get(`${azureBaseUrl}/domains`);

      dispatch({ type: AZURE_DOMAINS_LOADING, payload: false });
      dispatch({ type: AZURE_DOMAINS_SUCCESS, payload: response.data });

      if (callback) callback("success", response.data);
      return response;
    } catch (error: any) {
      dispatch({ type: AZURE_DOMAINS_LOADING, payload: false });
      dispatch({
        type: AZURE_DOMAINS_ERROR,
        payload: error?.response?.data || error?.message,
      });

      if (callback) callback("failure", error?.response?.data);
      return { error };
    }
  };
};